// ShipMarker.tsx — A ship on the galaxy map: a constant-on-screen-size icon + nametag. An
// in-system (transit) ship reads as activity at its star; a ship crossing a hyperlane
// GLIDES along the lane between the two stars. Clickable: selecting opens the ship's info
// pane. Mirrors StarMarker's sprite approach (sizeAttenuation off, no DOM).
import { useEffect, useMemo, useRef } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import { AdditiveBlending, Vector3 } from 'three';
import type { Group, Mesh, PerspectiveCamera } from 'three';
import type { ThreeEvent } from '@react-three/fiber';
import type { Galaxy } from '../types';
import type { Ship as ShipData } from '../state/economy';
import { gameClock } from '../state/gameClock';
import { useGame } from '../state/context';
import { glowTexture } from './glow';
import { labelText, LABEL_CANVAS_RATIO } from './labelText';
import { hyperlaneGalaxyPos } from './shipCourse';
import { shipName } from './shipNames';
import { trackShip, untrackShip } from '../state/shipTrack';

const LABEL_PX = 12; // on-screen text height
const DOT_PX = 18; // on-screen icon diameter
const SHIP_TINT = '#8fe9d0';
const HIT_R = 0.6; // world-space click target radius (galaxy zoom)
const PARK_LIFT = 0.6; // an in-system ship's icon floats just off its star

interface Props {
  galaxy: Galaxy;
  ship: ShipData;
  /** Icon colour (owner); defaults to the civilian teal. */
  tint?: string;
  /** Overrides the nametag (e.g. a grouped fleet "4 · Thal Dominion"). */
  label?: string;
}

function pxScale(px: number, camera: PerspectiveCamera, viewportHeight: number): number {
  const fov = camera.fov ?? 50;
  return (px * 2 * Math.tan((fov * Math.PI) / 360)) / viewportHeight;
}

export function ShipMarker({ galaxy, ship, tint = SHIP_TINT, label: labelOverride }: Props) {
  const { hover, hoveredId, selectedShipId, selectShip } = useGame();
  const camera = useThree((s) => s.camera) as PerspectiveCamera;
  const viewportHeight = useThree((s) => s.size.height);
  const glow = useMemo(glowTexture, []);
  const labelStr = labelOverride ?? `▸ ${shipName(ship)}`;
  const label = useMemo(() => labelText(labelStr || ' '), [labelStr]);
  const groupRef = useRef<Group>(null);
  const hitRef = useRef<Mesh>(null);
  const pos = useMemo(() => new Vector3(), []);
  const sys = useMemo(() => galaxy.systems.find((s) => s.id === ship.systemId), [galaxy, ship.systemId]);
  const selected = selectedShipId === ship.id;
  const hovered = hoveredId === ship.id;
  const lit = selected || hovered;
  const dot = pxScale(DOT_PX * (lit ? 1.35 : 1), camera, viewportHeight);
  const sy = pxScale(LABEL_PX * LABEL_CANVAS_RATIO, camera, viewportHeight);

  // Glide along the lane while in hyperlane; otherwise sit just off the ship's star.
  useEffect(() => () => untrackShip(ship.id), [ship.id]);
  // Priority -1: publish the drawn position before CameraRig's follow reads it this frame.
  useFrame(() => {
    const g = groupRef.current;
    if (!g) return;
    if (ship.phase === 'hyperlane') {
      if (hyperlaneGalaxyPos(galaxy, ship, gameClock.time, pos)) g.position.copy(pos);
    } else if (sys) {
      g.position.set(sys.position[0], sys.position[1] + PARK_LIFT, sys.position[2]);
    }
    if (ship.phase === 'hyperlane') trackShip(ship.id, g.position);
  }, -1);

  const initial: [number, number, number] = sys
    ? [sys.position[0], sys.position[1] + PARK_LIFT, sys.position[2]]
    : [0, 0, 0];

  const onOver = (e: ThreeEvent<PointerEvent>) => {
    e.stopPropagation();
    hover(ship.id);
    document.body.style.cursor = 'pointer';
  };
  const onOut = () => {
    hover(null);
    document.body.style.cursor = 'auto';
  };
  const onClick = (e: ThreeEvent<MouseEvent>) => {
    e.stopPropagation();
    // An in-system (transit) ship: clicking it flies into its system. A ship crossing a
    // hyperlane stays on the galaxy map (just opens its pane).
    selectShip(ship.id, ship.phase === 'transit' ? ship.systemId : undefined);
  };

  return (
    <group ref={groupRef} position={initial}>
      {/* Invisible click target — only for ships crossing a hyperlane. A ship inside a system
          sits on top of its star (the galaxy camera looks down on the disk), so a target there
          would swallow the star's hover and click; you reach those ships by entering the system
          (or from the fleet list) instead. */}
      {ship.phase === 'hyperlane' ? (
        <mesh ref={hitRef} onClick={onClick} onPointerOver={onOver} onPointerOut={onOut}>
          <sphereGeometry args={[HIT_R, 8, 6]} />
          <meshBasicMaterial transparent opacity={0} depthWrite={false} />
        </mesh>
      ) : null}
      <sprite scale={[dot, dot, 1]} renderOrder={3} raycast={() => null}>
        <spriteMaterial
          map={glow}
          color={tint}
          transparent
          opacity={lit ? 1 : 0.95}
          depthWrite={false}
          depthTest={false}
          blending={AdditiveBlending}
          toneMapped={false}
          sizeAttenuation={false}
        />
      </sprite>
      {labelStr ? (
      <sprite center-x={0.5} center-y={-0.7} scale={[sy * label.aspect, sy, 1]} renderOrder={4} raycast={() => null}>
        <spriteMaterial
          map={label.texture}
          transparent
          opacity={lit ? 1 : 0.82}
          depthWrite={false}
          depthTest={false}
          toneMapped={false}
          sizeAttenuation={false}
        />
      </sprite>
      ) : null}
    </group>
  );
}
